import React, { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, MapPin, Quote, User } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { TEAM } from "@/data/site";

export default function TeamMember() {
  const { slug } = useParams();
  const index = TEAM.findIndex((m) => m.slug === slug);
  const member = TEAM[index];
  const navigate = useNavigate();

  // Auto scroll to top when switching between officers
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  // 404 Guard Clause
  if (!member) {
    return (
      <div
        data-testid="team-member-notfound"
        className="max-w-7xl mx-auto px-6 md:px-12 pt-48 pb-40 text-center"
      >
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-michma mb-4 font-extrabold">
          404 Error
        </p>
        <h1 className="font-heading font-extrabold text-4xl md:text-6xl tracking-tighter text-michma-ink">
          Officer profile not found.
        </h1>
        <button
          onClick={() => navigate(-1)}
          data-testid="back-to-team-404"
          className="inline-flex items-center gap-2 mt-8 bg-michma text-white rounded-full px-8 py-4 font-semibold hover:bg-michma-hover transition-colors shadow-md cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" /> Back to organizational chart
        </button>
      </div>
    );
  }

  // Calculate infinite circular navigation for previous/next links
  const prev = TEAM[(index - 1 + TEAM.length) % TEAM.length];
  const next = TEAM[(index + 1) % TEAM.length];

  // Safely normalise bio whether provided as a String or an Array
  const bioParagraphs = Array.isArray(member.bio)
    ? member.bio
    : typeof member.bio === "string"
    ? member.bio.split("\n\n").filter(Boolean)
    : [];

  return (
    <div data-testid="team-member-page" className="bg-slate-50/50 min-h-screen">
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-32 md:pt-40 pb-20 md:pb-28">
        {/* Back Link */}
        <Link
          to="/team"
          data-testid="back-to-team"
          className="group inline-flex items-center gap-2 font-medium text-slate-500 hover:text-michma transition-colors mb-12"
        >
          <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
          Back to organizational chart
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Portrait Column */}
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
              className="lg:sticky lg:top-28 rounded-[2rem] overflow-hidden aspect-[4/5] bg-slate-100 border-2 border-slate-200/80 shadow-md flex items-center justify-center"
            >
              {member.image ? (
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="flex flex-col items-center justify-center text-slate-400 gap-3">
                  <User className="w-16 h-16 stroke-1" />
                  <span className="font-mono text-xs uppercase tracking-widest">
                    MICHMA Leader
                  </span>
                </div>
              )}
            </motion.div>
          </div>

          {/* Officer Details Column */}
          <div className="lg:col-span-7">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-michma font-extrabold mb-5">
              {member.role}
            </p>

            <h1 className="font-heading font-extrabold text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] text-michma-ink">
              <MaskedLines lines={member.name.split(" ")} delay={0.1} />
            </h1>

            {member.location && (
              <Reveal delay={0.3}>
                <p className="mt-6 inline-flex items-center gap-2 text-slate-500 font-medium">
                  <MapPin className="w-4 h-4 text-michma shrink-0" />
                  {member.location}
                </p>
              </Reveal>
            )}

            {member.quote && (
              <Reveal delay={0.35}>
                <blockquote className="mt-10 rounded-3xl bg-michma-soft/60 border border-michma/20 p-8 shadow-xs">
                  <Quote className="w-6 h-6 text-michma mb-3" />
                  <p className="font-heading text-2xl lg:text-3xl tracking-tight leading-snug text-michma-ink italic">
                    "{member.quote}"
                  </p>
                </blockquote>
              </Reveal>
            )}

            {/* Bio Paragraphs */}
            {bioParagraphs.length > 0 && (
              <div className="mt-10 space-y-5">
                {bioParagraphs.map((paragraph, i) => (
                  <Reveal key={i} delay={0.4 + i * 0.05}>
                    <p className="text-lg leading-relaxed text-slate-700">
                      {paragraph}
                    </p>
                  </Reveal>
                ))}
              </div>
            )}

            {/* Focus & Responsibilities */}
            {member.focus?.length > 0 && (
              <Reveal delay={0.5}>
                <div className="mt-10 pt-8 border-t border-slate-200/80">
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500 font-bold mb-4">
                    Key Portfolio Focus
                  </p>
                  <div className="flex flex-wrap gap-2.5">
                    {member.focus.map((f) => (
                      <span
                        key={f}
                        className="rounded-full border border-michma/30 bg-michma-soft text-michma px-4 py-2 text-sm font-semibold shadow-xs"
                      >
                        {f}
                      </span>
                    ))}
                  </div>
                </div>
              </Reveal>
            )}
          </div>
        </div>
      </section>

      {/* Prev / Next Pagination Controls */}
      <section className="border-t border-slate-200/80 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 grid grid-cols-2 gap-6">
          <Link
            to={`/team/${prev.slug}`}
            data-testid="prev-member"
            className="group flex flex-col items-start hover:opacity-90 transition-opacity"
          >
            <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-slate-500 font-bold mb-2">
              <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1 text-michma" />
              Previous Officer
            </span>
            <span className="font-heading font-bold text-xl md:text-2xl tracking-tight text-michma-ink group-hover:text-michma transition-colors">
              {prev.name}
            </span>
          </Link>

          <Link
            to={`/team/${next.slug}`}
            data-testid="next-member"
            className="group flex flex-col items-end text-right hover:opacity-90 transition-opacity"
          >
            <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-slate-500 font-bold mb-2">
              Next Officer
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1 text-michma" />
            </span>
            <span className="font-heading font-bold text-xl md:text-2xl tracking-tight text-michma-ink group-hover:text-michma transition-colors">
              {next.name}
            </span>
          </Link>
        </div>
      </section>
    </div>
  );
}